<template>
  <DetailsSidebar
    v-model:visible="visible"
    :title="drawerTitle"
    :width="isBulk ? '500px' : '560px'"
    test-id="alert-details-sidebar"
  >
    <!-- ─── SINGLE-ALERT: 3-region layout ─── -->
    <template v-if="!isBulk">
      <div class="h-full flex flex-col min-h-0 overflow-y-auto">
        <!-- Region 1: Chart -->
        <section
          class="p-4 border-b border-surface-200 dark:border-surface-700"
          data-test-id="alert-details-chart"
        >
          <template v-if="chartSpec">
            <div class="aspect-square w-full">
              <AnalyticsChart
                :spec="chartSpec"
                :merchant-id="String(singleAlert!.MERCHANT_ID)"
                :date-from="chartDateFrom"
                :date-to="chartDateTo"
                :pinnable="false"
              />
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col items-center justify-center py-6 text-center">
              <p class="text-sm text-surface-500 dark:text-surface-400">
                No chart available for this event type — see metadata below.
              </p>
            </div>
          </template>
        </section>

        <!-- Region 2: Metadata strip -->
        <section
          class="p-4 border-b border-surface-200 dark:border-surface-700"
          data-test-id="alert-details-metadata"
        >
          <h3 class="text-sm font-semibold text-surface-700 dark:text-surface-200 uppercase tracking-wide mb-3">
            Alert Details
          </h3>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-3">
            <div data-test-id="alert-meta-event-type">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Event Type
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                {{ formatEventType(singleAlert!.EVENT_TYPE) }}
              </dd>
            </div>
            <div data-test-id="alert-meta-threshold">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Threshold
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                {{ formatAlertType(singleAlert!.THRESHOLD, singleAlert!.EVENT_TYPE) }}
              </dd>
            </div>
            <div data-test-id="alert-meta-observed">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Observed
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                {{ formatAlertType(singleAlert!.STATISTIC, singleAlert!.EVENT_TYPE) }}
              </dd>
            </div>
            <div data-test-id="alert-meta-tolerance">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Tolerance
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                {{ formatTolerance(singleAlert!.TOLERANCE) }}
              </dd>
            </div>
            <div data-test-id="alert-meta-significance">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Significance
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                {{ formatSignificance(singleAlert!.SIGNIFICANCE) }}
              </dd>
            </div>
            <div data-test-id="alert-meta-alert-age">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Alert Age
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                {{ alertAge }}
              </dd>
            </div>
            <div class="col-span-2" data-test-id="alert-meta-merchant">
              <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
                Supplier
              </dt>
              <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
                <RouterLink
                  :to="{ name: 'fccMerchantDetails', params: { id: singleAlert!.MERCHANT_ID } }"
                  class="hover:underline"
                >
                  {{ singleAlert!.MERCHANT_NAME || `Supplier #${singleAlert!.MERCHANT_ID}` }}
                  ({{ singleAlert!.MERCHANT_ID }})
                </RouterLink>
              </dd>
            </div>
          </dl>
        </section>

        <!-- Region 3: Action form. Stacks inside the outer wrapper's scroll;
             no inner flex-1/overflow (which collapses to 0 inside an
             overflow-y-auto parent and hides the action options). -->
        <main class="bg-surface-0 dark:bg-surface-900">
          <div
            role="button"
            class="py-4 px-2 flex items-center justify-between bg-surface-0 dark:bg-surface-900 hover:bg-surface-50 dark:hover:bg-surface-800"
            @click="showActionDetails = !showActionDetails"
          >
            <span class="text-base font-medium"> Select an Action </span>
            <ChevronDownIcon
              class="w-5 h-5"
              :class="{ 'rotate-180': showActionDetails }"
            />
          </div>
          <Transition name="fade">
            <div v-show="showActionDetails" class="space-y-2">
              <div class="flex flex-col gap-3 px-3">
                <label
                  :for="`action-${action.value}`"
                  v-for="action in actionOptions"
                  :key="action.value"
                  class="flex items-start gap-3 p-3 border rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors cursor-pointer"
                  :class="{
                    'border-rch-black bg-surface-100 dark:bg-surface-800/20 dark:border-surface-400': selectedActions.includes(
                      action.value,
                    ),
                    'border-surface-200 dark:border-surface-700': !selectedActions.includes(action.value),
                    'border-red-300 dark:border-red-700': selectedActions.length === 0 && submitted,
                  }"
                >
                  <Checkbox
                    v-model="selectedActions"
                    :inputId="`action-${action.value}`"
                    :value="action.value"
                    class="mt-1"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="block text-sm font-medium text-surface-900 dark:text-surface-0">
                      {{ action.label }}
                    </div>
                    <p class="text-xs text-surface-600 dark:text-surface-300 mt-1 leading-relaxed">
                      {{ action.description }}
                    </p>
                    <div v-if="action.isFinalAction" class="mt-2">
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                      >
                        Final Action
                      </span>
                    </div>
                  </div>
                </label>
              </div>
              <small
                v-if="selectedActions.length === 0 && submitted"
                class="p-error mt-2 block"
              >
                Please select at least one action
              </small>
            </div>
          </Transition>
        </main>
      </div>
    </template>

    <!-- ─── BULK MODE: existing layout (unchanged) ─── -->
    <template v-else>
      <div class="h-full flex flex-col min-h-0">
        <!-- Selected Alerts Panel -->
        <header size="small">
          <div
            role="button"
            class="py-4 px-2 flex items-center justify-between bg-surface-0 dark:bg-surface-900 hover:bg-surface-50 dark:hover:bg-surface-800"
            @click="showSelectedAlerts = !showSelectedAlerts"
          >
            <span class="text-base font-medium">
              Selected Alerts ({{ selectedAlerts.length }})
            </span>
            <ChevronDownIcon
              class="w-5 h-5"
              :class="{ 'rotate-180': showSelectedAlerts }"
            />
          </div>
          <Transition name="fade">
            <div
              v-show="showSelectedAlerts"
              class="max-h-[20vh] overflow-y-auto space-y-2"
            >
              <div
                v-for="alert in selectedAlerts"
                :key="alert.ALERT_ID"
                class="flex flex-col p-3 bg-surface-50 dark:bg-surface-800 rounded text-sm border border-surface-200 dark:border-surface-700"
              >
                <div class="flex items-center justify-between mb-2 w-full">
                  <span class="font-medium">{{
                    alert.MERCHANT_NAME || `Supplier #${alert.MERCHANT_ID ?? ''}`
                  }}</span>
                  {{ formatSignificance(alert.SIGNIFICANCE) }}
                </div>
                <div class="flex items-center justify-between w-full">
                  <div class="text-xs text-surface-500 dark:text-surface-400 mb-1 w-1/2">
                    ID: {{ alert.MERCHANT_ID ?? '-' }}
                  </div>
                  <div class="text-xs text-surface-500 dark:text-surface-400 w-1/2 text-end">
                    {{ formatEventType(alert.EVENT_TYPE) }}
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </header>

        <!-- Select an Action panel -->
        <main class="z-10 bg-surface-0 dark:bg-surface-900 flex-1 flex flex-col min-h-0">
          <div
            role="button"
            class="py-4 px-2 flex items-center justify-between bg-surface-0 dark:bg-surface-900 hover:bg-surface-50 dark:hover:bg-surface-800"
            @click="showActionDetails = !showActionDetails"
          >
            <span class="text-base font-medium"> Select an Action </span>
            <ChevronDownIcon
              class="w-5 h-5"
              :class="{ 'rotate-180': showActionDetails }"
            />
          </div>
          <Transition name="fade">
            <div
              v-show="showActionDetails"
              class="flex-1 min-h-0 overflow-y-auto space-y-2"
            >
              <div class="flex flex-col gap-3 px-3">
                <label
                  :for="`action-${action.value}`"
                  v-for="action in actionOptions"
                  :key="action.value"
                  class="flex items-start gap-3 p-3 border rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors cursor-pointer"
                  :class="{
                    'border-rch-black bg-surface-100 dark:bg-surface-800/20 dark:border-surface-400': selectedActions.includes(
                      action.value,
                    ),
                    'border-surface-200 dark:border-surface-700': !selectedActions.includes(action.value),
                    'border-red-300 dark:border-red-700': selectedActions.length === 0 && submitted,
                  }"
                >
                  <Checkbox
                    v-model="selectedActions"
                    :inputId="`action-${action.value}`"
                    :value="action.value"
                    class="mt-1"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="block text-sm font-medium text-surface-900 dark:text-surface-0">
                      {{ action.label }}
                    </div>
                    <p class="text-xs text-surface-600 dark:text-surface-300 mt-1 leading-relaxed">
                      {{ action.description }}
                    </p>
                    <div v-if="action.isFinalAction" class="mt-2">
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                      >
                        Final Action
                      </span>
                    </div>
                  </div>
                </label>
              </div>
              <small
                v-if="selectedActions.length === 0 && submitted"
                class="p-error mt-2 block"
              >
                Please select at least one action
              </small>
            </div>
          </Transition>
        </main>
      </div>
    </template>

    <!-- Footer: action details + submit (shared by both modes) -->
    <template #footer>
      <footer class="sticky bottom-0 bg-surface-0 dark:bg-surface-900 flex flex-col gap-3 px-2">
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
            Action Details/Comment <span class="text-red-500">*</span>
          </label>
          <Textarea
            v-model="actionDetails"
            placeholder="Describe the action being taken..."
            rows="4"
            class="w-full"
            :class="{ 'p-invalid': !actionDetails && submitted }"
          />
          <small v-if="!actionDetails && submitted" class="p-error">
            Please provide action details
          </small>
        </div>

        <div class="p-3 bg-surface-100 dark:bg-surface-800/20 rounded border border-surface-200 dark:border-surface-700/60">
          <h5 class="text-sm font-medium text-surface-900 dark:text-surface-200 mb-2">
            Action Summary
          </h5>
          <div class="text-xs text-rch-black dark:text-surface-200 space-y-1">
            <div v-if="selectedActions.length > 0">
              <strong>Actions:</strong>
              {{
                selectedActions
                  .map(
                    actionId =>
                      actionOptions.find(opt => opt.value === actionId)
                        ?.label || actionId,
                  )
                  .join(', ')
              }}
            </div>
            <div v-if="hasFinalAction"><strong>Final Action:</strong> Yes</div>
            <div>
              <strong>Alerts: </strong>
              <output data-test-id="action-summary-alerts"
                >{{ selectedAlerts.length }}
              </output>
              selected
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end gap-3">
          <Button label="Cancel" severity="secondary" @click="closeDrawer" />
          <Button
            label="Submit Action"
            :loading="submitting"
            :disabled="submitting"
            data-test-id="alert-submit-action-btn"
            @click="submitAction"
          />
        </div>
      </footer>
    </template>
  </DetailsSidebar>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { Button, Checkbox, Textarea } from 'primevue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { RouterLink } from 'vue-router'
import type { Alert, BulkActionItem } from '@/types/fcc'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import { useFCCMerchantStore } from '@/stores/fcc'
import { useFCCCaseStore } from '@/stores/fcc'
import { useFrontegg } from '@frontegg/vue'
import { formatUpperUnderscoreCase } from '@/util/util'
import DetailsSidebar from '@/components/fcc/DetailsSidebar.vue'
import AnalyticsChart from '@/components/fcc/charts/AnalyticsChart.vue'
import { getAnalyticsKind } from '@/util/fcc/eventTypeToAnalyticsKind'
import { CHART_SPECS } from '@/util/fcc/charts/chartSpecs'
import { defaultDateRange } from '@/util/fcc/charts/dateRange'

interface Props {
  visible: boolean
  selectedAlerts: Alert[]
}

interface ActionData {
  actionType: string
  actionDetails: string
  isFinalAction: boolean
  alertIds: (string | number)[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:visible': [value: boolean]
  'action-submitted': [data: ActionData]
}>()

const { formatEventType, formatSignificance, formatAlertType, formatTolerance } =
  useFCCFormatting()

// Store integration
const merchantStore = useFCCMerchantStore()
const { availableActions } = storeToRefs(merchantStore)
const { authState } = useFrontegg()

// Form state
const selectedActions = ref<string[]>([])
const actionDetails = ref<string>('')
const submitting = ref<boolean>(false)
const submitted = ref<boolean>(false)
const showSelectedAlerts = ref<boolean>(false)
const showActionDetails = ref<boolean>(true)

// Single vs bulk mode
const isBulk = computed(() => props.selectedAlerts.length > 1)
const singleAlert = computed(() => (isBulk.value ? null : props.selectedAlerts[0] ?? null))

const drawerTitle = computed(() =>
  isBulk.value
    ? `Action Multiple Alerts (${props.selectedAlerts.length})`
    : 'Action an Alert',
)

// Chart helpers for single-alert mode
const alertKind = computed(() =>
  singleAlert.value ? getAnalyticsKind(singleAlert.value.EVENT_TYPE) : null,
)

const chartSpec = computed(() => {
  if (!alertKind.value) return null
  return CHART_SPECS.find(s => s.kind === alertKind.value) ?? null
})

const chartRange = defaultDateRange(90)
const chartDateFrom = chartRange.date_from
const chartDateTo = chartRange.date_to

const alertAge = computed((): string => {
  const raw = singleAlert.value?.TIMESTAMP || singleAlert.value?.EVENT_DATE
  if (!raw) return '-'
  const now = Date.now()
  const ts = new Date(raw).getTime()
  const diffMs = now - ts
  if (diffMs < 0) return '-'
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    return diffHours <= 1 ? 'Less than 1 hour ago' : `${diffHours} hours ago`
  }
  return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
})

// Computed action options from store
const actionOptions = computed(() => {
  if (!availableActions.value || availableActions.value.length === 0) {
    return []
  }

  return availableActions.value.map(action => ({
    label: formatUpperUnderscoreCase(action.ACTION_TYPE),
    description: action.DESCRIPTION,
    value: action.ACTION_TYPE_ID.toString(),
    isFinalAction: action.IS_FINAL_ACTION,
  }))
})

// Computed properties
const hasFinalAction = computed(() => {
  return selectedActions.value.some(actionId => {
    const action = actionOptions.value.find(opt => opt.value === actionId)
    return action?.isFinalAction
  })
})

const visible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
})

function closeDrawer() {
  visible.value = false
  resetForm()
}

function resetForm() {
  selectedActions.value = []
  actionDetails.value = ''
  submitted.value = false
  submitting.value = false
}

async function submitAction() {
  // Atomic double-click prevention
  const wasAlreadyProcessing = submitting.value
  submitting.value = true
  if (wasAlreadyProcessing) return

  submitted.value = true

  if (selectedActions.value.length === 0 || !actionDetails.value) {
    submitting.value = false
    return
  }

  try {
    // Build bulk actions array
    const bulkActions: BulkActionItem[] = []

    for (const alert of props.selectedAlerts) {
      for (const actionId of selectedActions.value) {
        bulkActions.push({
          alert_id: Number(alert.ALERT_ID),
          action_type_id: Number(actionId),
          action_details: actionDetails.value,
        })
      }
    }

    // Execute all actions in a single bulk request
    await merchantStore.executeBulkActions(authState, { actions: bulkActions })

    const actionData: ActionData = {
      actionType: selectedActions.value.join(','),
      actionDetails: actionDetails.value,
      isFinalAction: hasFinalAction.value,
      alertIds: props.selectedAlerts.map(alert => alert.ALERT_ID),
    }

    emit('action-submitted', actionData)
    // Background refresh of cases list after any action
    try {
      const caseStore = useFCCCaseStore()
      // Fire-and-forget; we don't want to block the UI here
      void caseStore.refreshCases(authState)
    } catch (e) {
      // swallow background refresh errors
      console.warn('Failed to trigger cases refresh:', e)
    }
    closeDrawer()
  } catch (error) {
    console.error('Error submitting action:', error)
    // TODO: Show error toast
  } finally {
    submitting.value = false
  }
}
</script>
<style scoped></style>
